// hooks/useAudioPlayer.ts
import { useEffect, useRef, useState } from "react";

// Module-level variable to track currently playing audio
let currentlyPlayingAudio: HTMLAudioElement | null = null;

export function useAudioPlayer(audioSrc: string | null | undefined) {
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasError, setHasError] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Setup all event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioSrc) return;

    const handlePause = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (currentlyPlayingAudio === audio) {
        currentlyPlayingAudio = null;
      }
    };

    audio.addEventListener("pause", handlePause);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [audioSrc]);

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      audio.currentTime = 0;
      currentlyPlayingAudio = null;
    } else {
      // Pause any other currently playing audio
      if (currentlyPlayingAudio && currentlyPlayingAudio !== audio) {
        currentlyPlayingAudio.pause();
        currentlyPlayingAudio.currentTime = 0;
      }

      audio.currentTime = 0;
      audio.playbackRate = playbackSpeed;

      try {
        await audio.play();
        setIsPlaying(true);
        currentlyPlayingAudio = audio;
      } catch {
        setIsPlaying(false);
        setHasError(true);
      }
    }
  };

  const changePlaybackSpeed = (speed: number) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const clearError = () => setHasError(false);

  return {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    playbackSpeed,
    hasError,
    togglePlayback,
    changePlaybackSpeed,
    formatTime,
    clearError,
  };
}
